import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../../api/apiClient';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import ErrorBanner from '../../../components/ui/ErrorBanner';
import { formatDate } from '../../../utils/formatters';
import { st, colors } from '../shared/meetingStyles';
import { ClipboardIcon, StarIcon, CheckSquareIcon, BulbIcon, MegaphoneIcon, FileTextIcon, ClockIcon } from '../shared/Icons';

function Section({ icon: SectionIcon, title, empty, children }) {
  return (
    <div style={{ marginBottom: '1.3rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem' }}>
        <SectionIcon size={15} style={{ color: colors.faint }} />
        <span style={st.h3}>{title}</span>
      </div>
      {empty ? <div style={{ ...st.subtext, paddingLeft: '1.3rem' }}>None.</div> : <div style={{ paddingLeft: '1.3rem' }}>{children}</div>}
    </div>
  );
}

export default function LastMeetingSummarySection({ meetingId, refreshKey }) {
  const [session, setSession] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!meetingId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(`/meetings/${meetingId}/sessions/last-completed`);
      setSession(res.data.session);
      setSummary(res.data.summary);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) return <LoadingSpinner />;

  if (!session) {
    return (
      <div style={st.card}>
        <div style={st.cardHeader}><h2 style={{ ...st.h2, display: 'flex', alignItems: 'center', gap: '0.5rem' }}><ClipboardIcon size={18} /> Last meeting's summary</h2></div>
        <div style={st.emptyState}><ClipboardIcon size={28} style={{ color: colors.faint }} />No completed meetings yet.</div>
      </div>
    );
  }

  const stat = (label, value, StatIcon) => (
    <div style={{ textAlign: 'center', flex: 1 }}>
      <StatIcon size={20} style={{ color: 'rgba(255,255,255,0.85)', marginBottom: '0.25rem' }} />
      <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#fff', letterSpacing: '-0.02em' }}>{value}</div>
      <div style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.65)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
    </div>
  );

  return (
    <div style={st.card}>
      {error && <ErrorBanner message={error} />}
      <div style={st.cardHeader}>
        <h2 style={{ ...st.h2, display: 'flex', alignItems: 'center', gap: '0.5rem' }}><ClipboardIcon size={18} /> Last meeting's summary</h2>
        <span style={st.pill}>{formatDate(session.session_date)}</span>
      </div>
      <div style={{
        background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
        borderRadius: '0.9rem', padding: '1.2rem', display: 'flex', marginBottom: '1.3rem',
        boxShadow: '0 4px 16px rgba(79,70,229,0.25)',
      }}>
        {stat('Rating', summary.avgRating != null ? summary.avgRating.toFixed(1) : '—', StarIcon)}
        {stat('To-dos Done', summary.todosCompletedPct != null ? summary.todosCompletedPct + '%' : '—', CheckSquareIcon)}
        {stat('Issues Solved', summary.issuesSolvedCount, BulbIcon)}
        {stat('Total Time', summary.totalMinutes != null ? `${Math.floor(summary.totalMinutes / 60)}h${summary.totalMinutes % 60}m` : '—', ClockIcon)}
      </div>

      <Section icon={StarIcon} title="Ratings" empty={summary.ratings.length === 0}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {summary.ratings.map(r => (
            <span key={r.user_id} style={{ ...st.pill, fontWeight: '700' }}>{r.name}: <strong style={{ color: colors.primaryDark }}>&nbsp;{r.rating}</strong></span>
          ))}
        </div>
      </Section>

      <Section icon={CheckSquareIcon} title="To-dos completed" empty={summary.todosCompleted === 0}>
        <div style={st.subtext}>{summary.todosCompleted} to-do{summary.todosCompleted !== 1 ? 's' : ''} completed this meeting.</div>
      </Section>

      <Section icon={BulbIcon} title="Issues solved" empty={summary.issuesSolved.length === 0}>
        {summary.issuesSolved.map(i => (
          <div key={i.id} style={{ fontSize: '0.78rem', color: colors.text, padding: '0.2rem 0' }}>
            {i.owner_name && <strong>{i.owner_name}: </strong>}{i.title}
          </div>
        ))}
      </Section>

      <Section icon={MegaphoneIcon} title="Headlines" empty={summary.headlines.length === 0}>
        {summary.headlines.map(h => (
          <div key={h.id} style={{ fontSize: '0.78rem', color: colors.text, padding: '0.2rem 0' }}>
            {h.owner_name && <strong>{h.owner_name}: </strong>}{h.text}
          </div>
        ))}
      </Section>

      <Section icon={FileTextIcon} title="Notes" empty={!summary.notes}>
        <div style={st.subtext}>{summary.notes}</div>
      </Section>
    </div>
  );
}
